import { useEffect, useState } from 'react';
import { Alert, Button, Card, CardActionArea, Box, Chip, Stack, Typography } from '@mui/material';
import { MenuBookRounded, EventAvailableRounded } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SectionScheduleLine } from '../components/ScheduleText';
import { useAllAssignments, useEnrolledSections } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { courseColor } from './CourseShell';
import { usePortalViewing } from '../../../stores/portal-view.store';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantTimeZone } from '../../../stores/tenant.store';
import { coursesIndexGroups } from '../util/lifecycle';
import { useClassroomOn } from '../hooks/use-classroom-on';

export function CoursesIndexPage(): JSX.Element {
  const navigate = useNavigate();
  // «Ver portal»: sin invitación a inscribirse (no es de la vista).
  const viewing = usePortalViewing();
  const { t } = useTranslation('sis');
  const [searchParams, setSearchParams] = useSearchParams();
  // Mercado Pago redirects back here after pay_to_enroll checkout.
  const [payment, setPayment] = useState<'paid' | 'pending' | null>(
    searchParams.get('mpPaid') === '1'
      ? 'paid'
      : searchParams.get('mpPending') === '1'
        ? 'pending'
        : null,
  );
  useEffect(() => {
    if (searchParams.has('mpPaid') || searchParams.has('mpPending')) {
      searchParams.delete('mpPaid');
      searchParams.delete('mpPending');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paymentBanner =
    payment !== null ? (
      <Alert severity={payment === 'paid' ? 'success' : 'info'} onClose={() => setPayment(null)}>
        {payment === 'paid'
          ? t('portal.courses.paymentConfirmed')
          : t('portal.courses.paymentPending')}
      </Alert>
    ) : null;
  const sectionsQ = useEnrolledSections();
  const assignmentsQ = useAllAssignments();
  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  // LA MISMA REGLA DE CICLO DE VIDA QUE EL INICIO: arriba lo que está en curso o
  // por empezar; lo terminado, aparte, en «Cursos pasados»; lo archivado vive en
  // Historial. Antes un curso terminado se veía igual que uno en curso.
  const { current, past, archived } = coursesIndexGroups(
    sectionsQ.data?.enrolledSections ?? [],
    todayYmd,
  );
  const enrolled = [...current.map((c) => c.section), ...past];
  // Sin aula, la tarjeta no habla de tareas pendientes (g2).
  const classroom = useClassroomOn();
  const enrollOpen =
    !viewing &&
    sectionsQ.data?.activeTerm?.enrollmentOpen === true &&
    (sectionsQ.data?.availableSections?.length ?? 0) > 0;

  if (sectionsQ.isLoading || !classroom.settled) return <PortalLoading />;

  // Si la lectura falló, decirlo: «No tienes cursos inscritos» sería falso
  // para un alumno que sí los tiene.
  if (sectionsQ.isError) {
    return (
      <Stack spacing={2}>
        {paymentBanner}
        <Alert severity="error">{t('portal.courses.loadError')}</Alert>
      </Stack>
    );
  }

  if (enrolled.length === 0) {
    // Con cursos solo en Historial, «No tienes cursos inscritos» sería falso.
    return (
      <Stack spacing={2}>
        {paymentBanner}
        {archived > 0 ? (
          <PortalEmpty
            icon={<MenuBookRounded fontSize="inherit" />}
            title={t('portal.courses.onlyPastTitle')}
            body={t('portal.courses.onlyPastBody')}
          />
        ) : (
          <PortalEmpty
            icon={<MenuBookRounded fontSize="inherit" />}
            title={t('portal.courses.emptyTitle')}
            body={t('portal.courses.emptyBody')}
          />
        )}
        {archived > 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => navigate('/portal-v2/historial')}>
              {t('portal.courses.openHistory')}
            </Button>
          </Box>
        )}
        {enrollOpen && (
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              startIcon={<EventAvailableRounded />}
              onClick={() => navigate('/portal-v2/enroll')}
            >
              {t('portal.enroll.cta')}
            </Button>
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {paymentBanner}
      <Typography variant="h4" fontWeight={700} color="#e6edf3">
        {t('portal.courses.title')}
      </Typography>
      {enrollOpen && (
        <Alert
          severity="info"
          icon={<EventAvailableRounded />}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/portal-v2/enroll')}>
              {t('portal.enroll.cta')}
            </Button>
          }
        >
          {t('portal.enroll.bannerOpen')}
        </Alert>
      )}
      <CourseGrid
        items={current.map((c) => ({
          section: c.section,
          label: c.lifecycle === 'upcoming' ? 'upcoming' : null,
        }))}
        assignments={assignmentsQ.data ?? []}
        showPending={classroom.on}
        showSchedule={!classroom.on}
        onOpen={(id) => navigate(`/portal-v2/courses/${id}`)}
      />
      {past.length > 0 && (
        <Box>
          <Typography variant="overline" color="#8b949e" fontWeight={700}>
            {t('portal.dashboard.pastCourses')}
          </Typography>
          <CourseGrid
            items={past.map((section) => ({ section, label: 'concluded' as const }))}
            assignments={[]}
            showPending={false}
            showSchedule={false}
            onOpen={(id) => navigate(`/portal-v2/courses/${id}`)}
          />
        </Box>
      )}
    </Stack>
  );
}

type CourseCard = {
  id: string;
  courseCode: string;
  courseName: string;
  facultyName: string;
  credits: number;
  schedule?: Array<{ dayOfWeek: number; startTime: string; endTime: string; location?: string }>;
};

/** Las tarjetas de «Mis cursos». `label`: «Terminado» o «Próximamente», como en
 *  el Inicio; sin etiqueta, en curso. */
function CourseGrid({
  items,
  assignments,
  showPending,
  showSchedule,
  onOpen,
}: {
  items: Array<{ section: CourseCard; label: 'concluded' | 'upcoming' | null }>;
  assignments: Array<{ sectionId: string; studentSubmission?: { status?: string } | null }>;
  showPending: boolean;
  /** Sin aula, la línea del horario de la sección (g4). */
  showSchedule: boolean;
  onOpen: (sectionId: string) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        mt: 0.5,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
      }}
    >
      {items.map(({ section: s, label }) => {
        const color = courseColor(s.id);
        const pending = showPending
          ? assignments.filter((a) => {
              if (a.sectionId !== s.id) return false;
              const st = a.studentSubmission?.status;
              return !st || st === 'draft';
            }).length
          : 0;
        return (
          <Card
            key={s.id}
            sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}
          >
            <CardActionArea onClick={() => onOpen(s.id)}>
              <Box sx={{ height: 80, bgcolor: color, position: 'relative' }}>
                {label && (
                  <Chip
                    size="small"
                    label={
                      label === 'concluded'
                        ? t('portal.dashboard.cardFinished')
                        : t('portal.dashboard.cardComingSoon')
                    }
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="#8b949e">
                  {s.courseCode}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" noWrap>
                  {s.courseName}
                </Typography>
                <Typography variant="caption" color="#8b949e">
                  {s.facultyName}
                </Typography>
                {/* Sin aula, el horario de la sección (g4): «Horario por
                    confirmar» si no tiene. Con aula, la tarjeta de siempre. */}
                {showSchedule && (
                  <Box sx={{ mt: 1 }}>
                    <SectionScheduleLine schedule={s.schedule} />
                  </Box>
                )}
                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={t('portal.courses.creditsChip', { count: s.credits })}
                    variant="outlined"
                    sx={{ borderColor: '#30363d' }}
                  />
                  {pending > 0 && (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={t('portal.courses.pendingChip', { count: pending })}
                    />
                  )}
                </Stack>
              </Box>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}
